import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, mergeMap, of, throwError } from 'rxjs';
import { LocalStorageEnum } from '../../enums/token.enum';
import { AuthService } from '../nx-auth/auth.service';
import { environment } from 'src/environments/environment';

const excludedUrls = ['es.json', 'it.json', 'en.json', 'de.json', 'fr.json', '/api-systema/selfcare/otp'];

function getToken(authService: AuthService): Observable<string> {
  return of(authService.getLocalStorageAccessToken());
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const excludedUrlsRegex = excludedUrls.map((urlPattern) => new RegExp(urlPattern)) || [];
  const authService = inject(AuthService);
  let request = req;

  // 1. Verificar si la URL debe ser excluida del tipado de tokens
  const passThrough = !!excludedUrlsRegex.find((regex) => regex.test(req.url));
  if (passThrough) {
    return next(req);
  }

  // 2. 🌟 DETERMINACIÓN DINÁMICA DEL TIPO DE AUTORIZACIÓN 🌟
  let authorizationWord = 'Bearer'; // Por defecto

  // Si la petición va dirigida al dominio de Mavalue de este entorno
  if (environment.apiUrl && req.url.includes(environment.apiUrl)) {
    authorizationWord = 'Token';
  }
  // Si va dirigida al dominio de Billma de este entorno
  else if (environment.apiBillma && req.url.includes(environment.apiBillma)) {
    authorizationWord = 'Bearer';
  }
  // 🛡️ SALVAVIDAS: Por si la URL es relativa (ej: "/api/ndbx") y no incluye el dominio completo en la petición
  else {
    const mavalueKeywords = [
      'api/ndbx',
      'api/forms',
      'api/menus',
      'api/claimData',
      'api/assets',
      'api/fnol',
      'api/schedule'
    ];
    const isMavalueRelative = mavalueKeywords.some((keyword) => req.url.includes(keyword));
    if (isMavalueRelative) {
      authorizationWord = 'Token';
    }
  }

  // 3. Configuración del Content-Type para subida de ficheros
  const isDocumentUpload = req.url.includes('/api-systema/bridge/api/files');
  const contentType = isDocumentUpload ? 'multipart/form-data' : 'application/json';

  return getToken(authService).pipe(
    mergeMap((token: string) => {
      const lang = localStorage.getItem(LocalStorageEnum.LANGUAGE) || 'fr';

      // Clonamos la request aplicando los headers calculados dinámicamente
      const headersConfig: Record<string, string> = {
        'Accept-Language': lang,
        Authorization: `${authorizationWord} ${token}`
      };

      // Si el cuerpo no es FormData, inyectamos el Content-Type de forma segura
      if (!(req.body instanceof FormData)) {
        headersConfig['Content-Type'] = contentType;
      }

      request = req.clone({
        setHeaders: headersConfig
      });

      return next(request);
    }),
    catchError((error: HttpErrorResponse) => {
      return throwError(() => error);
    })
  );
};
